/**
 * PAPPO — ReportModal
 * Um RESPONSÁVEL denuncia outro usuário: motivo + descrição + evidências
 * (prints/fotos, enviadas a /uploads). A denúncia cai na fila do console /staff.
 * Ao enviar, mostra uma confirmação verde clara antes de fechar.
 */
import { useCallback, useRef, useState } from 'react';
import { X, ImagePlus, ShieldAlert, Loader2, CheckCircle2 } from 'lucide-react';
import { uploadImage } from '../../services/uploads';
import {
  createReport,
  REPORT_REASONS,
  type ReportReason,
} from '../../services/reports';
import styles from './ReportModal.module.css';

interface ReportModalProps {
  targetId: number;
  targetName: string;
  onClose: () => void;
  onDone: () => void;
}

type Evidence = { url: string; preview: string };

export function ReportModal({ targetId, targetName, onClose, onDone }: ReportModalProps) {
  const [reason, setReason] = useState<ReportReason>('assedio');
  const [description, setDescription] = useState('');
  const [evidence, setEvidence] = useState<Evidence[]>([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFiles = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setError(null);
    let falhas = 0;
    // Copiado ANTES de qualquer await (a FileList do input é limpa depois).
    for (const file of Array.from(files).slice(0, 10)) {
      const res = await uploadImage(file);
      if (res.success && res.data?.attachment_url) {
        const url = res.data.attachment_url;
        const preview = URL.createObjectURL(file);
        setEvidence((prev) => [...prev, { url, preview }]);
      } else {
        falhas += 1;
      }
    }
    setUploading(false);
    if (falhas > 0) {
      setError(
        falhas === 1
          ? 'Não foi possível anexar uma das imagens. Tente novamente.'
          : `Não foi possível anexar ${falhas} imagens. Tente novamente.`
      );
    }
  }, []);

  const removeEvidence = useCallback((i: number) => {
    setEvidence((prev) => {
      const next = [...prev];
      const [gone] = next.splice(i, 1);
      if (gone) URL.revokeObjectURL(gone.preview);
      return next;
    });
  }, []);

  const handleSubmit = useCallback(async () => {
    if (submitting || uploading) return;
    setSubmitting(true);
    setError(null);
    const res = await createReport(
      targetId,
      reason,
      description.trim(),
      evidence.map((e) => e.url)
    );
    setSubmitting(false);
    if (res.success) {
      onDone();
      setSent(true);
    } else {
      setError(res.error || 'Não foi possível enviar a denúncia.');
    }
  }, [submitting, uploading, targetId, reason, description, evidence, onDone]);

  // ── Confirmação (verde, clara) ────────────────────────────────
  if (sent) {
    return (
      <div className={styles.overlay} onClick={onClose} role="dialog" aria-modal="true">
        <div className={styles.card} onClick={(e) => e.stopPropagation()}>
          <div className={styles.success}>
            <CheckCircle2 size={56} className={styles.successIcon} />
            <h2 className={styles.successTitle}>Denúncia enviada!</h2>
            <p className={styles.successText}>
              A equipe de moderação vai analisar{' '}
              {evidence.length > 0 ? 'as evidências ' : ''}e tomar as providências.
              Obrigado por ajudar a manter o PAPPO seguro.
            </p>
            <button type="button" className={styles.doneBtn} onClick={onClose}>
              Concluir
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.overlay} onClick={onClose} role="dialog" aria-modal="true">
      <div className={styles.card} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <ShieldAlert size={20} className={styles.headerIcon} />
          <h2 className={styles.title}>Denunciar {targetName}</h2>
          <button className={styles.close} onClick={onClose} aria-label="Fechar">
            <X size={18} />
          </button>
        </div>

        <label className={styles.label}>Motivo</label>
        <select
          className={styles.select}
          value={reason}
          onChange={(e) => setReason(e.target.value as ReportReason)}
        >
          {REPORT_REASONS.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>

        <label className={styles.label}>Descrição (o que aconteceu)</label>
        <textarea
          className={styles.textarea}
          value={description}
          maxLength={2000}
          rows={4}
          placeholder="Descreva a situação. Anexe prints como evidência abaixo."
          onChange={(e) => setDescription(e.target.value)}
        />

        <label className={styles.label}>Evidências (prints / fotos)</label>
        <button
          type="button"
          className={styles.dropzone}
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? (
            <>
              <Loader2 size={22} className={styles.spin} />
              <span className={styles.dropzoneText}>A anexar…</span>
            </>
          ) : (
            <>
              <ImagePlus size={22} />
              <span className={styles.dropzoneText}>Adicionar prints / fotos</span>
              <span className={styles.dropzoneHint}>
                Toque para escolher imagens (até 10)
              </span>
            </>
          )}
        </button>

        {evidence.length > 0 && (
          <div className={styles.thumbs}>
            {evidence.map((e, i) => (
              <div key={i} className={styles.thumb}>
                <img src={e.preview} alt={`Evidência ${i + 1}`} />
                <button
                  type="button"
                  className={styles.thumbRemove}
                  onClick={() => removeEvidence(i)}
                  aria-label="Remover evidência"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        )}

        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = '';
          }}
        />

        {error && <div className={styles.error}>{error}</div>}

        <div className={styles.actions}>
          <button type="button" className={styles.cancel} onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className={styles.submit}
            onClick={handleSubmit}
            disabled={submitting || uploading}
          >
            {submitting ? 'A enviar…' : 'Enviar denúncia'}
          </button>
        </div>
      </div>
    </div>
  );
}
